import { BaseService } from './base-service';
import { config } from '../config';
import { CallResponse } from '../types';
import { FlutterUtils } from "@/hooks/useFlutterBridge";

export class AuthService extends BaseService {
  public async checkUserLogin(): Promise<boolean> {
    if (this.heyUtils.userAccessToken && 
        Date.now() < this.heyUtils.expiresAt && 
        this.heyUtils.user_id && 
        this.heyUtils.user_id.length > 10) {
      console.debug('User token already exists and is valid, user public id:', this.heyUtils.user_id);
      await this.heyUtils.getUserProfile();
      return true;
    }

    if (this.heyUtils.userAccessToken && Date.now() > this.heyUtils.expiresAt) {
      console.debug('User token has expired, trying to refresh it');
      const refreshed = await this.callRefreshToken();
      if (refreshed) {
        await this.heyUtils.getUserProfile();
      }
      return refreshed;
    }

    if (!this.heyUtils.userAccessToken) {
      return false;
    }

    return false;
  }

  public async loginUserPassword(email: string, password: string): Promise<CallResponse> {
    console.debug('Starting password login for email:', email);
    const passwordResponse = await this.callHeyApiTech('POST', `/v3/login`, {
      userEmail: email,
      password: password,
      deviceId: 'hey-linkup',
    });

    if (!passwordResponse.success) {
      return { 
        success: false, 
        error: `Invalid email or password. ${passwordResponse.error}` 
      };
    }
    
    passwordResponse.data.publicId = passwordResponse.data.userPublicId;
    localStorage.removeItem("userName");
    this.handleUserAuthResponse(passwordResponse.data, email);
    
    await this.heyUtils.getUserProfile();
    
    return { success: true };
  }

  public async requestOTP(email: string): Promise<CallResponse> {
    console.debug('Request OTP for :', { email });
    const otpResponse = await this.callHeyApiTech('POST', `/v3/user/authenticate`, { email });
    console.debug('Authentication response details:', otpResponse);
    
    if (!otpResponse.success) {
      return { 
        success: false, 
        error: `Failed to send OTP. ${otpResponse.error}` 
      };
    }
    
    return { success: true };
  }

  public async validateOTP(code: string, email: string): Promise<CallResponse> {
    console.debug('Starting OTP verification for email:', email);
    const otpValidateResponse = await this.callHeyApiTech('POST', `/v3/user/${email}/validateLogin`, {
      accessCode: code,
      deviceId: 'hey-linkup',
    });

    if (!otpValidateResponse.success) {
      return { 
        success: false, 
        error: `Invalid OTP code. ${otpValidateResponse.error}` 
      };
    }
    localStorage.removeItem("userName");
    this.handleUserAuthResponse(otpValidateResponse.data, email);
    
    await this.heyUtils.getUserProfile();
    
    return { success: true };
  }

  public async registerUser(request: any): Promise<CallResponse> {
    console.debug('Starting user registration...');
    const registerResponse = await this.callHeyApiTech('POST', `/v3/user`, request);

    if (!registerResponse.success) {
      return { 
        success: false, 
        error: `Failed to register user. ${registerResponse.error}` 
      };
    }
    
    return { success: true };
  }

  public async enableNotifications(user_id: string): Promise<CallResponse> {
    console.debug('enableNotifications...');
    const response = await this.callHeyApiUser('PATCH', `/v3/user/${user_id}/settings`, {
      notificationsEnabled : true
    });
    if (!response.success) {
      return { 
        success: false, 
        error: `Failed to set Notification Enabled. ${response.error}` 
      };
    }
    return { success: true };
  }

  public async setUserPassword(email: string, password: string): Promise<CallResponse> {
    console.debug('Setting password for email:', email);
    const passwordResponse = await this.callHeyApiUser('PUT', `/v3/user/credentials`, {
      password: password,
      userEmail: email
    });

    if (!passwordResponse.success) {
      return { 
        success: false, 
        error: `Failed to set password. ${passwordResponse.error}` 
      };
    }
    
    return { success: true };
  }

  public async updatePassword(newPassword: string): Promise<boolean> {
    console.debug('Updating user password...');
    let result = await this.callHeyApiUser('PUT', `/v3/user/credentials`, {
      userEmail: this.heyUtils.user_email,
      password: newPassword
    });
    
    if (!result.success) {
      console.error("📊 UPDATE PASSWORD FAILED:", result.error);
      throw new Error('Failed to update password: ' + result.error);
    }
    
    console.debug("📊 UPDATE PASSWORD SUCCESS");
    return true;
  }

  public async refreshToken(): Promise<boolean> {
    console.debug('Refreshing user token using refresh token...');
    if (!this.heyUtils.refreshToken) {
      console.error('No refresh token available');
      return false;
    }

    const refreshResponse = await this.callHeyApiTech('POST', `/v3/user/refreshToken`, {
      refreshToken: this.heyUtils.refreshToken
    });

    if (!refreshResponse.success) {
      console.error('Failed to refresh token:', refreshResponse.error);
      return false;
    }

    console.debug('Token refreshed successfully');
    
    try {
      localStorage.setItem(config.LS_KEY_TOKEN, refreshResponse.data.token);
      localStorage.setItem(config.LS_KEY_REFRESH_TOKEN, refreshResponse.data.refreshToken);
      localStorage.setItem(config.LS_KEY_TOKEN_EXPIRES, String(Date.now() + 1000 * 60 * 60 * 24));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      localStorage.clear();
    }
  

    this.heyUtils.userAccessToken = refreshResponse.data.token;
    this.heyUtils.refreshToken = refreshResponse.data.refreshToken;
    this.heyUtils.expiresAt = Date.now() + 1000 * 60 * 60 * 24;
    
    return true;
  }

  public async callRefreshToken(): Promise<boolean> {
    try {
      return await this.refreshToken();
    } catch (error) {
      console.error('Error refreshing token:', error);
      return false;
    }
  }

  public async callHeyApiTech(method: string, url: string, body: any): Promise<CallResponse> {
    return super.callHeyApiTech(method, url, body);
  }

  public async callHeyApiUser(method: string, url: string, body: any): Promise<CallResponse> {
    return super.callHeyApiUser(method, url, body);
  }

  public async callHeyApiOAGTech(method: string, url: string, body: any): Promise<CallResponse> {
    return super.callHeyApiOAGTech(method, url, body);
  }

  private handleUserAuthResponse(data: any, email: string) {
    console.debug('Storing user tokens in local storage...');
    console.debug('Token:', data.token);
    console.debug('Public ID:', data.publicId);
    
    try {
      localStorage.setItem(config.LS_KEY_TOKEN, data.token);
      localStorage.setItem(config.LS_KEY_PUBLIC_ID, data.publicId);
      localStorage.setItem(config.LS_KEY_PUBLIC_EMAIL, email);
      localStorage.setItem(config.LS_KEY_REFRESH_TOKEN, data.refreshToken);
      localStorage.setItem(config.LS_KEY_TOKEN_EXPIRES, String(Date.now() + 1000 * 60 * 60 * 24));
    } catch (error) {
      console.error('Error saving flight details to localStorage:', error);
      localStorage.clear();
    }
    this.heyUtils.userAccessToken = data.token;
    this.heyUtils.refreshToken = data.refreshToken;
    this.heyUtils.expiresAt = Date.now() + 1000 * 60 * 60 * 24;
    this.heyUtils.user_id = data.publicId;
    this.heyUtils.user_email = email;
    
    FlutterUtils.requestFirebaseToken();
  }
}
